import {
  GetCurrentSequenceFrom,
  HomeWorkoutSequence,
  IAudioTip,
  ILeaderboardRow,
  ISessionSequenceInfo,
  LeaderboardManager,
  Phase,
  UserProfile,
  UserWorkoutSession,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import {
  CardioOrResistance, IDeeplinkParameters, ILiveStreamOptions, Instructor,
} from 'fitbeat.models';
import { produce } from 'immer';
import { IAuthState, ITrackingState } from '../auth/types';
import { IBookingWizardState, IDemoBooking, IOpenInviteDetails } from '../Bookings/reducers';
import { getConfig } from '../config';
import { IFamilyMembersState } from '../FamilyMembers';
import { INutritionState } from '../Nutrition/reducer';
import { IPaymentsState } from '../payments';
import { IPlannerState } from '../Planner';
import { ISummaryDashboardState } from '../SummaryDashboard/reducer';
import { types as UIActionTypes } from '../ui/redux/uiActions';
import { IUiReducerState } from '../ui/redux/uiReducer';
import { Logger } from '../utils';
import { IWeightTargetState } from '../WeightTarget/reducer';
import {
  ACTIVATE_LEADERBOARD,
  AUDIO_TIP_PLAYED,
  BEEP_VOLUME_CHANGED,
  CameraPermissionRequest,
  CHANGE_ORIENTATION,
  CHANGE_SELECTED_INSTRUCTOR,
  CHANGE_WORKOUT_SETTINGS,
  CLOSE_CANCEL_WORKOUT,
  CLOSE_DEVICE_SETUP,
  CLOSE_DISPLAY_SETTINGS,
  CLOSE_HOME_WORKOUT,
  CLOSE_INSTRUCTOR_AND_DISCARD_CHANGES,
  CLOSE_MUSIC_PLATFORMS,
  CLOSE_SETUP_SCREEN,
  CLOSE_WORKOUT_SETTINGS,
  COMPLETE_WORKOUT_REQUEST,
  COMPLETE_WORKOUT_SUCCESS,
  DEACTIVATE_LEADERBOARD,
  DISABLE_MIRROR,
  ENABLE_MIRROR,
  GET_INSTRUCTOR_LIST_SUCCESS,
  HOME_WARMDOWN_STARTED_FAILURE,
  HOME_WARMDOWN_STARTED_REQUEST,
  HOME_WARMDOWN_STARTED_SUCCESS,
  HOME_WORKOUT_TO_REVIEW,
  INVITE_FRIENDS_FAILURE,
  INVITE_FRIENDS_REQUEST,
  INVITE_FRIENDS_SUCCESS,
  LEADERBOARD_LAYOUT_CHANGED,
  LOAD_WORKOUT_VIDEO_FAILURE,
  LOAD_WORKOUT_VIDEO_SUCCESS,
  MIRROR_SETUP_SCREEN_OPENED,
  NAVIGATE_TO_DASHBOARD_AFTER_REVIEW,
  ON_PRELOAD_OF_EXERCISES_COMPLETED,
  ON_PRELOAD_OF_EXERCISES_PROGRESS_UPDATED,
  ON_WARMUPORFIRSTEXERCISE_JUST_STARTED_PLAYING,
  OPEN_CANCEL_WORKOUT,
  OPEN_DEVICE_SETUP,
  OPEN_DISPLAY_SETTINGS,
  OPEN_HOME_WORKOUT_FAILURE,
  OPEN_HOME_WORKOUT_REQUEST,
  OPEN_HOME_WORKOUT_SUCCESS,
  OPEN_MUSIC_PLATFORMS,
  OPEN_SETUP_SCREEN,
  OPEN_WORKOUT_SETTINGS,
  PAUSE_HOME_WORKOUT,
  PRE_WORKOUT_CANCEL_REQUEST,
  PRE_WORKOUT_SCREEN_CLOSE_REQUEST,
  RESTART_ASSET_CACHING,
  RESUME_HOME_WORKOUT,
  RETRYING_CURRENT_VIDEO,
  SAVE_AND_CLOSE_INSTRUCTOR_SCREEN_REQUEST,
  SAVE_HOME_WORKOUT_SETTINGS_FAILURE,
  SAVE_HOME_WORKOUT_SETTINGS_REQUEST,
  SAVE_HOME_WORKOUT_SETTINGS_SUCCESS,
  SET_DEFAULT_INSTRUCTOR,
  SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_FAILURE,
  SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_REQUEST,
  SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_SUCCESS,
  SET_USER_SETTINGS_OPTION_FAILURE,
  SET_USER_SETTINGS_OPTION_REQUEST,
  SET_USER_SETTINGS_OPTION_SUCCESS,
  START_WORKOUT,
  TOGGLE_INSTRUCTOR_AUDIO_ENABLED,
  UPDATE_LEADERBOARD_FAILURE,
  UPDATE_LEADERBOARD_REQUEST,
  UPDATE_LEADERBOARD_SUCCESS,
  UPDATE_WORKOUT_SCREEN,
  UPDATE_WORKOUT_SETTINGS_LOADER,
  USER_UPDATED_METRIC_INFO,
  VOICE_VOLUME_CHANGED,
  WORKOUT_SCREEN_TAPPED,
  WORKOUT_TYPE_WARNING,
  WORKOUT_VIDEO_CACHED,
} from './actions';
import { IGroupCallState, IMusicState } from './components';
import { CALL_CONNECTED } from './components/VideoCalling';
import { HEART_RATE_MONITOR_CHANGED } from './heartRateActions';
import { IHeartRateBeltStoreState } from './heartRateReducer';
import { DeviceSetupScreens } from './utils/DeviceSetupScreens';
import { IStrengthAssessmentState } from '../StrengthAssessment';
import { GymResolver } from '../services';

const config = getConfig();

export type Orientation = 'PORTRAIT' | 'LANDSCAPE';

export enum Orientations {
  portrait = 'PORTRAIT',
  landscape = 'LANDSCAPE',
}

export interface IAppStore {
  ui: IUiReducerState;
  auth: IAuthState;
  music: IMusicState;
  groupCalling: IGroupCallState;
  homeWorkouts: IHomeWorkoutsStoreState;
  heartRateMonitor: IHeartRateBeltStoreState;
  planner: IPlannerState;
  bookings: {
    bookings: IOpenInviteDetails;
    bookingWizard: IBookingWizardState;
    demo: IDemoBooking;
  };
  weightTarget: IWeightTargetState;
  familyMembers: IFamilyMembersState;
  deepLinkParamaters: IDeeplinkParameters;
  payments: IPaymentsState;
  nutrition: INutritionState;
  summaryDashboard: ISummaryDashboardState;
  strengthAssessment: IStrengthAssessmentState;
  tracking: ITrackingState;
}

export interface IHomeWorkoutsStoreState {
  currentPlayingVideo: string;
  currentSessionSequenceInfo: ISessionSequenceInfo | null;
  homeWorkoutSession: HomeWorkoutSequence | null;
  shouldLoadNextVideo: boolean;
  homeWorkoutLoading: boolean;
  preloadOfExercisesLoading: boolean;
  preloadOfExercisesDownloadProgress: number | null;
  openMusicPlatformLoading: boolean;
  homeWorkoutLoadingError: string | null;
  homeWorkoutVideoError: string | null;
  workoutSettingsModalIsOpen: boolean;
  MusicPlatformsModelIsOpen: boolean;
  DisplaySettingsModelIsOpen: boolean;
  cancelWorkoutModalIsOpen: boolean;
  updatedWorkoutSettings: WorkoutSettings | null;
  workoutSettingsIsUpdating: boolean;
  workoutSettingsUpdateError: string | null;
  shouldCloseWorkout: boolean;
  workoutPauseStartTime: Date | null;
  audioTipToPlay: string | null;
  userHasToggledMenuDescription: boolean;
  menuDescriptionIsOpen: boolean;
  homeWorkoutId: string | null;
  shouldSaveMetrics: boolean;
  isSavingMetrics: boolean;
  hasSavedMetrics: boolean;
  shouldApplyWarmdownState: boolean;
  isProcessingHomeWarmdownStarted: boolean;
  hasProcessedHomeWarmdownState: boolean;
  inOrPastWarmdown: boolean;
  inOrBeforeWarmup: boolean;
  isWorkoutFinished: boolean;
  deviceSetupIsOpen: boolean;
  warmupOrFirstExerciseHasStartedPlaying: boolean;
  userProfile: UserProfile | null;
  instructorAudioEnabled: boolean;
  beepVolume: number;
  voiceVolume: number;
  selectedInstructor: Instructor | null;
  defaultInstructor: string;
  instructorList: Instructor[];
  largeParticipantViewIsOpen: boolean;
  isGroupWorkout: boolean;
  homeWorkoutToReviewId: string | null;
  afterHomeWorkoutReviewRedirectToDashboard: boolean;
  submittedHomeWorkoutReviewId: string | null;
  workoutIsCompleting: boolean;
  currentSetupScreen: DeviceSetupScreens | null;
  isPastWarmdown: boolean;
  orientation: Orientation;
  genericMusicVolume: number;
  isFetchingLeaderboard: boolean;
  leaderboardFetchedAt: Date;
  fullResponseLeaderboardRows: ILeaderboardRow[];
  leaderboardRows: ILeaderboardRow[];
  groupCallLeaderboardRows: { [key: string]: ILeaderboardRow };
  mirrorIsActive: boolean;
  muiscStartedAndAudioDisabledAtStartOfWorkout: boolean;
  maxAmountOfLeaderboardRows: number;
  onlyGenericMusicAllowed: boolean;
  secondsUntilWorkoutCloses: number;
  waitingAtEndOfWorkoutPercentageComplete: number;
  currentSetOfTips: IAudioTip[];
  isFirstWorkout: boolean;
  userHasStartedWorkout: boolean;
  isLeaderboardActivated: boolean;
  showWorkoutSummaryDeviceSetupScreen: boolean;
  loadingShareLink: boolean;
  hasOpenedScreenCasting: boolean;
  hasOpenedExerciseMirror: boolean;
  hasCameraPermission: boolean;
  workoutTypeToShowWarningFor?: CardioOrResistance;
  livestreamOptions?: ILiveStreamOptions;
  betaFeatureChangeLoading: boolean;
}

export const createInitialValues = (): IHomeWorkoutsStoreState => ({
  currentSessionSequenceInfo: null,
  homeWorkoutSession: null,
  currentPlayingVideo: '',
  shouldLoadNextVideo: false,
  homeWorkoutLoading: true,
  preloadOfExercisesLoading: true,
  preloadOfExercisesDownloadProgress: null,
  openMusicPlatformLoading: true,
  homeWorkoutLoadingError: null,
  homeWorkoutVideoError: null,
  updatedWorkoutSettings: null,
  workoutSettingsModalIsOpen: false,
  MusicPlatformsModelIsOpen: false,
  DisplaySettingsModelIsOpen: false,
  cancelWorkoutModalIsOpen: false,
  workoutSettingsIsUpdating: false,
  workoutSettingsUpdateError: null,
  shouldCloseWorkout: false,
  workoutPauseStartTime: null,
  audioTipToPlay: null,
  menuDescriptionIsOpen: false,
  userHasToggledMenuDescription: false,
  homeWorkoutId: null,
  shouldSaveMetrics: false,
  isSavingMetrics: false,
  hasSavedMetrics: false,
  shouldApplyWarmdownState: false,
  isProcessingHomeWarmdownStarted: false,
  hasProcessedHomeWarmdownState: false,
  inOrPastWarmdown: false,
  inOrBeforeWarmup: false,
  isWorkoutFinished: false,
  deviceSetupIsOpen: false,
  warmupOrFirstExerciseHasStartedPlaying: false,
  userProfile: null,
  instructorAudioEnabled: true,
  beepVolume: 100,
  voiceVolume: 100,
  selectedInstructor: null,
  defaultInstructor: '',
  instructorList: [],
  largeParticipantViewIsOpen: false,
  isGroupWorkout: false,
  homeWorkoutToReviewId: null,
  submittedHomeWorkoutReviewId: null,
  workoutIsCompleting: false,
  currentSetupScreen: null,
  isPastWarmdown: false,
  orientation: 'PORTRAIT',
  genericMusicVolume: 1,
  isFetchingLeaderboard: false,
  fullResponseLeaderboardRows: [],
  leaderboardRows: [],
  leaderboardFetchedAt: new Date(),
  groupCallLeaderboardRows: {},
  mirrorIsActive: false,
  muiscStartedAndAudioDisabledAtStartOfWorkout: false,
  maxAmountOfLeaderboardRows: 5,
  onlyGenericMusicAllowed: false,
  afterHomeWorkoutReviewRedirectToDashboard: true,
  secondsUntilWorkoutCloses: 0,
  waitingAtEndOfWorkoutPercentageComplete: 0,
  currentSetOfTips: [],
  isFirstWorkout: false,
  userHasStartedWorkout: false,
  isLeaderboardActivated: true,
  showWorkoutSummaryDeviceSetupScreen: true,
  loadingShareLink: false,
  hasOpenedScreenCasting: false,
  hasOpenedExerciseMirror: false,
  hasCameraPermission: false,
  betaFeatureChangeLoading: false,
});

export const NUMBER_OF_SEQUENCES_UNTIL_WARMUP = 1;
export const NUMBER_OF_SEQUENCES_UNTIL_WARMUP_WITH_TALK = 2;
const WARMDOWN_AMOUNT_OF_SESSION_SEQUENCES = 2;

export const homeWorkoutsReducer = produce((draft = createInitialValues(), action: any): IHomeWorkoutsStoreState => {
  switch (action.type) {
    case HOME_WORKOUT_TO_REVIEW: {
      if (action.payload && action.payload.isDemoBooking) {
        break;
      }

      if (action.payload) {
        draft.homeWorkoutToReviewId = action.payload.workoutId;
        draft.afterHomeWorkoutReviewRedirectToDashboard = action.payload.navigateToDashboardAfterReview;
        draft.submittedHomeWorkoutReviewId = null;
      } else {
        draft.submittedHomeWorkoutReviewId = draft.homeWorkoutToReviewId;
        draft.homeWorkoutToReviewId = null;
        draft.afterHomeWorkoutReviewRedirectToDashboard = true;
      }
      break;
    }
    case UIActionTypes.RATE_WORKOUT_MODAL: {
      if (!action.visible) {
        draft.homeWorkoutToReviewId = null;
        draft.afterHomeWorkoutReviewRedirectToDashboard = false;
        draft.submittedHomeWorkoutReviewId = null;
      }
      break;
    }
    case OPEN_HOME_WORKOUT_REQUEST: {
      draft = createInitialValues();
      const {
        booking,
        userProfile,
        defaultLeaderBoardState,
        defaultMirrorStateFromLocalStorage,
        hasCameraPermission,
      } = action.payload;
      let isFirstTimeBooking = false;
      if (draft.homeWorkoutSession) {
        isFirstTimeBooking = Boolean(draft.homeWorkoutSession.isDemoBooking);
      }

      const initialMirrorState = defaultMirrorStateFromLocalStorage === undefined
        ? hasCameraPermission : JSON.parse(defaultMirrorStateFromLocalStorage);

      draft.homeWorkoutSession = {
        ...booking,
        sequences: [],
        gym: GymResolver.getByName(booking.gymName),
        workoutSettings: booking.settings,
        isDemoBooking: booking.isDemo,
      };

      draft.isGroupWorkout = !!UserWorkoutSession.getOpenInviteReference(booking, GymResolver.getByName);
      draft.deviceSetupIsOpen = true;
      draft.userProfile = userProfile;
      draft.workoutPauseStartTime = booking.isInstant ? new Date() : null;
      draft.isFirstWorkout = booking.isDemo;
      draft.isLeaderboardActivated = JSON.parse(defaultLeaderBoardState);
      draft.mirrorIsActive = initialMirrorState;
      draft.hasCameraPermission = action.payload.hasCameraPermission;
      draft.hasOpenedScreenCasting = action.payload.hasOpenedScreenCasting;
      draft.livestreamOptions = booking.livestreamOptions
          || booking.pairedSession?.livestreamOptions;

      if (userProfile.homeWorkoutInstructor) {
        draft.defaultInstructor = userProfile.homeWorkoutInstructor;
      }
      if (draft.instructorList) {
        draft.selectedInstructor = draft.instructorList
          .find((instructor) => instructor.id === booking.settings.homeWorkoutInstructor);
      }
      break;
    }
    case CLOSE_HOME_WORKOUT: {
      const homeWorkoutToReviewAfterCloseId = draft.homeWorkoutToReviewId;
      draft = createInitialValues();
      draft.homeWorkoutToReviewId = homeWorkoutToReviewAfterCloseId;
      draft.shouldCloseWorkout = true;
      break;
    }
    case OPEN_HOME_WORKOUT_SUCCESS: {
      const { homeWorkoutSession } = action.payload;
      const { homeWorkoutId } = action.payload;
      homeWorkoutSession.endTime = new Date(homeWorkoutSession.endTime);
      homeWorkoutSession.originalEndTime = new Date(homeWorkoutSession.originalEndTime);
      homeWorkoutSession.startTimeOfFirstExercise = new Date(homeWorkoutSession.startTimeOfFirstExercise);
      homeWorkoutSession.startTime = new Date(homeWorkoutSession.startTime);
      const nextSessionSequenceInfo = GetCurrentSequenceFrom(
        homeWorkoutSession.endTime,
        homeWorkoutSession.workoutDurationInMs,
        action.payload.now,
        homeWorkoutSession.sequences,
      );
      draft.homeWorkoutId = homeWorkoutId;
      homeWorkoutSession.endTime = getEndtimeWithDurationAdded(
        homeWorkoutSession,
        action.payload.workoutLoadDifference,
      );
      draft.homeWorkoutSession = homeWorkoutSession;
      draft.homeWorkoutLoading = false;
      draft.homeWorkoutLoadingError = null;
      draft.isGroupWorkout = !!homeWorkoutSession.groupCallPairedId;
      draft.onlyGenericMusicAllowed = !!homeWorkoutSession.groupCallPairedId;
      draft.currentSessionSequenceInfo = nextSessionSequenceInfo;
      const isInOrBeforeWarmup = inOrBeforeWarmup(draft);
      draft.inOrBeforeWarmup = isInOrBeforeWarmup;
      break;
    }
    case ON_PRELOAD_OF_EXERCISES_COMPLETED: {
      draft.preloadOfExercisesLoading = false;
      break;
    }
    case START_WORKOUT: {
      draft.userHasStartedWorkout = true;
      draft.showWorkoutSummaryDeviceSetupScreen = false;
      closeDeviceSetup(draft);
      break;
    }
    case OPEN_HOME_WORKOUT_FAILURE: {
      draft.homeWorkoutId = null;
      draft.homeWorkoutSession = null;
      draft.homeWorkoutLoading = false;
      draft.homeWorkoutLoadingError = action.payload;
      break;
    }
    case CALL_CONNECTED:
    case UPDATE_WORKOUT_SCREEN: {
      if (!draft.homeWorkoutSession || draft.homeWorkoutLoading) {
        break;
      }

      const workoutIsPaused = draft.workoutPauseStartTime;
      if (workoutIsPaused) {
        break;
      }

      const isReducerEnabled = getConfig().workoutUpdateReducerEnabled;

      const {
        endTime,
        sequences,
        workoutDurationInMs,
        workoutId,
      } = draft.homeWorkoutSession;
      const now = action.payload.currentTime as Date;
      const { debug } = action.payload;
      const timestamp = now.getTime();
      const endTimestamp = endTime.getTime();
      const timeDifference = timestamp - endTimestamp;
      const fiveMinutes = 5 * 60 * 1000;
      const forceEndingEarlier = timeDifference > 30000 && !isReducerEnabled;
      if (timeDifference >= fiveMinutes || forceEndingEarlier) {
        draft.homeWorkoutSession = null;
        draft.homeWorkoutLoading = false;
        draft.homeWorkoutLoadingError = null;
        draft.currentPlayingVideo = '';
        draft.shouldLoadNextVideo = false;
        draft.shouldCloseWorkout = true;
        draft.homeWorkoutToReviewId = workoutId;
        draft.afterHomeWorkoutReviewRedirectToDashboard = true;
        draft.submittedHomeWorkoutReviewId = null;
        Logger.debug('Workout reducer:UPDATE_WORKOUT_SCREEN end of workout');
        break;
      }

      if (!isReducerEnabled) {
        Logger.debug('Workout reducer:UPDATE_WORKOUT_SCREEN is disabled');
        break;
      }

      draft.secondsUntilWorkoutCloses = Math.floor((endTimestamp + fiveMinutes - timestamp) / 1000);
      if (timeDifference < 0) {
        draft.waitingAtEndOfWorkoutPercentageComplete = 0;
      } else {
        draft.waitingAtEndOfWorkoutPercentageComplete = timeDifference / fiveMinutes * 100;
      }

      const nextSessionSequenceInfo = GetCurrentSequenceFrom(
        endTime,
        workoutDurationInMs,
        action.payload.currentTime,
        sequences,
      );

      const NUMBER_OF_SEQUENCES_AFTER_WARMDOWN = 1;
      const isPastWarmdown = currentSequenceIsInOrAfter(
        draft,
        NUMBER_OF_SEQUENCES_AFTER_WARMDOWN,
      );
      const isInOrPastWarmdown = currentSequenceIsInOrAfter(
        draft,
        WARMDOWN_AMOUNT_OF_SESSION_SEQUENCES,
      );
      const shouldSaveMetrics = isPastWarmdown
        && !draft.isSavingMetrics
        && !draft.hasSavedMetrics;

      const shouldProcessHomeWarmdownState = isInOrPastWarmdown
        && !draft.isProcessingHomeWarmdownStarted
        && !draft.hasProcessedHomeWarmdownState;

      const largeParticipantViewShouldOpen = !!(
        draft.isGroupWorkout
          && nextSessionSequenceInfo?.currentSequence.showLargeViewInGroupWorkout
          && action.payload.callIsConnected
      );
      const loadNextVideo = shouldLoadNextVideo(
        draft.currentSessionSequenceInfo,
        nextSessionSequenceInfo,
        draft,
      );
      const nextAudioTip = nextAudioTipToPlay(
        draft,
        nextSessionSequenceInfo,
      );
      const openMenuDescription = shouldOpenMenuDescription(draft, nextSessionSequenceInfo);

      const isInOrBeforeWarmup = inOrBeforeWarmup(draft);

      draft.currentSessionSequenceInfo = nextSessionSequenceInfo;
      draft.menuDescriptionIsOpen = openMenuDescription;
      draft.shouldLoadNextVideo = loadNextVideo;
      draft.audioTipToPlay = nextAudioTip;
      draft.shouldSaveMetrics = shouldSaveMetrics;
      draft.shouldApplyWarmdownState = shouldProcessHomeWarmdownState;
      draft.inOrPastWarmdown = isInOrPastWarmdown;
      draft.inOrBeforeWarmup = isInOrBeforeWarmup;
      draft.isPastWarmdown = isPastWarmdown;

      const isWorkoutFinished = timestamp >= endTimestamp;
      draft.isWorkoutFinished = isWorkoutFinished;
      if (draft.largeParticipantViewIsOpen && !largeParticipantViewShouldOpen) {
        draft.currentPlayingVideo = '';
        draft.shouldLoadNextVideo = true;
      }
      draft.largeParticipantViewIsOpen = largeParticipantViewShouldOpen;
      const musicShouldBeDucked = shouldDuckMusic(draft.currentSessionSequenceInfo);
      const normalVolume = draft.homeWorkoutSession.isStreamingWorkout ? config.streamingWorkoutVolume : 1;
      const duckingVolume = draft.homeWorkoutSession.isStreamingWorkout
        ? config.streamingWorkoutDuckedVolume : config.genericMusicDuckedVolumePercentage;
      draft.genericMusicVolume = musicShouldBeDucked ? duckingVolume : normalVolume;
      draft.onlyGenericMusicAllowed = draft.isGroupWorkout;
      Logger.debug(`Workout timings: reducer:UPDATE_WORKOUT_SCREEN ${debug} done in ${Date.now() - timestamp}`);
      break;
    }
    case RETRYING_CURRENT_VIDEO: {
      break;
    }
    case LOAD_WORKOUT_VIDEO_SUCCESS: {
      draft.currentPlayingVideo = action.payload.videoUrl;
      draft.homeWorkoutVideoError = null;
      draft.shouldLoadNextVideo = false;
      break;
    }
    case LOAD_WORKOUT_VIDEO_FAILURE: {
      draft.currentPlayingVideo = '';
      draft.shouldLoadNextVideo = false;
      draft.homeWorkoutVideoError = action.payload;
      break;
    }
    case OPEN_WORKOUT_SETTINGS: {
      if (!draft.homeWorkoutSession) {
        break;
      }

      draft.workoutSettingsModalIsOpen = true;
      draft.updatedWorkoutSettings = draft.homeWorkoutSession.workoutSettings;
      break;
    }
    case CLOSE_WORKOUT_SETTINGS: {
      draft.workoutSettingsModalIsOpen = false;
      draft.updatedWorkoutSettings = null;
      break;
    }
    case OPEN_MUSIC_PLATFORMS: {
      if (!draft.homeWorkoutSession) {
        break;
      }

      draft.MusicPlatformsModelIsOpen = true;
      break;
    }
    case CLOSE_MUSIC_PLATFORMS: {
      draft.MusicPlatformsModelIsOpen = false;
      break;
    }

    case CHANGE_SELECTED_INSTRUCTOR: {
      if (draft.selectedInstructor !== action.payload.selectedInstructor) {
        draft.selectedInstructor = action.payload.selectedInstructor;
        if (draft.defaultInstructor !== action.payload.selectedInstructor.id) {
          draft.defaultInstructor = '';
        }
      }
      break;
    }
    case OPEN_DISPLAY_SETTINGS: {
      if (!draft.homeWorkoutSession) {
        break;
      }

      draft.DisplaySettingsModelIsOpen = true;
      break;
    }
    case CLOSE_DISPLAY_SETTINGS: {
      draft.DisplaySettingsModelIsOpen = false;
      break;
    }

    case OPEN_CANCEL_WORKOUT: {
      if (!draft.homeWorkoutSession) {
        break;
      }
      draft.cancelWorkoutModalIsOpen = true;
      break;
    }
    case CLOSE_CANCEL_WORKOUT: {
      draft.cancelWorkoutModalIsOpen = false;
      break;
    }
    case UPDATE_WORKOUT_SETTINGS_LOADER: {
      draft.workoutSettingsIsUpdating = action.payload;
      draft.preloadOfExercisesDownloadProgress = 0;
      break;
    }
    case SAVE_HOME_WORKOUT_SETTINGS_REQUEST: {
      draft.workoutSettingsIsUpdating = true;
      draft.workoutSettingsUpdateError = null;
      draft.currentPlayingVideo = '';
      draft.preloadOfExercisesDownloadProgress = 0;
      break;
    }
    case SAVE_HOME_WORKOUT_SETTINGS_SUCCESS: {
      const homeWorkoutSession = action.payload.updatedWorkoutSession;
      homeWorkoutSession.endTime = new Date(homeWorkoutSession.endTime);
      homeWorkoutSession.originalEndTime = new Date(homeWorkoutSession.originalEndTime);
      homeWorkoutSession.startTimeOfFirstExercise = new Date(homeWorkoutSession.startTimeOfFirstExercise);
      homeWorkoutSession.startTime = new Date(homeWorkoutSession.startTime);

      draft.homeWorkoutSession = homeWorkoutSession,
      draft.updatedWorkoutSettings = null;
      draft.currentSetupScreen = null;
      draft.workoutSettingsModalIsOpen = false;
      draft.workoutSettingsIsUpdating = false;
      draft.workoutSettingsUpdateError = null;
      draft.preloadOfExercisesDownloadProgress = 0;
      draft.preloadOfExercisesLoading = true;
      draft.selectedInstructor = draft.instructorList
        .find((instructor) => instructor.id === homeWorkoutSession.workoutSettings.homeWorkoutInstructor) ?? null;

      draft.workoutPauseStartTime = !homeWorkoutSession.homeWorkoutStartedAt
        ? action.payload.now : draft.workoutPauseStartTime;

      const pauseDuration = !draft.workoutPauseStartTime
        ? 0 : action.payload.now.getTime() - draft.workoutPauseStartTime.getTime();
      const endTime = getEndtimeWithDurationAdded(draft.homeWorkoutSession, pauseDuration);
      const nextSessionSequenceInfo = GetCurrentSequenceFrom(
        endTime,
        homeWorkoutSession.workoutDurationInMs,
        action.payload.now,
        homeWorkoutSession.sequences,
      );
      draft.currentSessionSequenceInfo = nextSessionSequenceInfo;
      break;
    }
    case SAVE_HOME_WORKOUT_SETTINGS_FAILURE: {
      draft.workoutSettingsIsUpdating = false;
      draft.workoutSettingsUpdateError = action.payload;
      break;
    }
    case CHANGE_WORKOUT_SETTINGS: {
      draft.updatedWorkoutSettings = action.payload.newWorkoutSettings;
      break;
    }
    case PAUSE_HOME_WORKOUT: {
      draft.workoutPauseStartTime = action.payload.pauseStartTime;
      break;
    }
    case WORKOUT_SCREEN_TAPPED: {
      draft.deviceSetupIsOpen = true;
      break;
    }
    case RESUME_HOME_WORKOUT: {
      draft.showWorkoutSummaryDeviceSetupScreen = false;
      closeDeviceSetup(draft);

      if (!draft.workoutPauseStartTime || !draft.homeWorkoutSession) {
        break;
      }
      const pauseDuration = action.payload.resumeTime.getTime() - draft.workoutPauseStartTime.getTime();

      draft.workoutPauseStartTime = null;
      draft.homeWorkoutSession.endTime = getEndtimeWithDurationAdded(draft.homeWorkoutSession, pauseDuration);
      break;
    }
    case AUDIO_TIP_PLAYED: {
      draft.audioTipToPlay = null;
      break;
    }
    case COMPLETE_WORKOUT_REQUEST: {
      draft.isSavingMetrics = true;
      draft.hasSavedMetrics = false;
      draft.shouldSaveMetrics = false;
      draft.cancelWorkoutModalIsOpen = false;
      draft.workoutIsCompleting = action.payload.showLoading;
      break;
    }
    case COMPLETE_WORKOUT_SUCCESS: {
      draft.isSavingMetrics = false;
      draft.hasSavedMetrics = true;
      draft.shouldSaveMetrics = false;
      draft.shouldCloseWorkout = false;
      draft.currentPlayingVideo = '';
      draft.shouldLoadNextVideo = true;
      draft.cancelWorkoutModalIsOpen = false;
      break;
    }
    case CLOSE_DEVICE_SETUP: {
      closeDeviceSetup(draft);
      break;
    }
    case OPEN_DEVICE_SETUP: {
      draft.isFirstWorkout = false;
      draft.deviceSetupIsOpen = true;
      break;
    }
    case TOGGLE_INSTRUCTOR_AUDIO_ENABLED:
      draft.instructorAudioEnabled = action.payload.enabled;
      break;
    case BEEP_VOLUME_CHANGED:
      draft.beepVolume = action.payload.volume;
      break;
    case VOICE_VOLUME_CHANGED:
      draft.voiceVolume = action.payload.volume;
      break;
    case ON_PRELOAD_OF_EXERCISES_PROGRESS_UPDATED:
      if (draft.preloadOfExercisesLoading && (typeof action.payload === 'number')) {
        const roundedPercentProgress = Math.round(action.payload * 100);
        const current = draft.preloadOfExercisesDownloadProgress === null
          ? 0 : draft.preloadOfExercisesDownloadProgress;
        const minPercentDiffToUpdate = 5;
        if (roundedPercentProgress - current >= minPercentDiffToUpdate) {
          draft.preloadOfExercisesDownloadProgress = roundedPercentProgress;
        }
      }
      break;
    case RESTART_ASSET_CACHING: {
      draft.preloadOfExercisesLoading = true;
      draft.preloadOfExercisesDownloadProgress = null;
      break;
    }
    case WORKOUT_VIDEO_CACHED:
      const sequenceIndexes = action.payload.sequenceIndexes as number[];
      if (!draft.homeWorkoutSession) {
        break;
      }

      for (const sequenceIndex of sequenceIndexes) {
        if (draft.homeWorkoutSession.sequences[sequenceIndex]) {
          (draft.homeWorkoutSession.sequences[sequenceIndex] as any).cached = true;
        }
      }
      break;
    case USER_UPDATED_METRIC_INFO:
      if (!draft.userProfile) {
        break;
      }
      draft.userProfile = action.payload.userProfile;
      break;
    case HEART_RATE_MONITOR_CHANGED:
      if (!draft.userProfile) {
        break;
      }
      draft.userProfile.heartRateMonitors = action.payload.heartRateMonitors;
      break;
    case OPEN_SETUP_SCREEN:
      const { screen } = action.payload;
      if (screen === DeviceSetupScreens.MyWorkout
        && draft.homeWorkoutSession
        || screen === DeviceSetupScreens.WorkoutTypeWarning
      ) {
        draft.updatedWorkoutSettings = draft.homeWorkoutSession.workoutSettings;
      }

      if (screen === DeviceSetupScreens.ScreenCasting) {
        draft.hasOpenedScreenCasting = true;
      }

      draft.currentSetupScreen = screen;
      break;
    case WORKOUT_TYPE_WARNING: {
      const { cardioOrResistance } = action.payload;
      draft.workoutTypeToShowWarningFor = cardioOrResistance;
      break;
    }
    case CLOSE_SETUP_SCREEN:
      if (action.payload.fromSetupScreen && action.payload.fromSetupScreen !== draft.currentSetupScreen) {
        break;
      }
      draft.currentSetupScreen = null;
      break;
    case CLOSE_INSTRUCTOR_AND_DISCARD_CHANGES: {
      draft.currentSetupScreen = null;
      const currentSelectedInstructor = action.payload.instructorList
        .find((item: Instructor) => item.name === action.payload.instructor);
      draft.selectedInstructor = currentSelectedInstructor;
      break;
    }
    case SAVE_AND_CLOSE_INSTRUCTOR_SCREEN_REQUEST:
      draft.currentSetupScreen = null;
      break;
    case CHANGE_ORIENTATION:
      const { orientation } = action.payload;
      let result: Orientation;
      if (orientation === 'FACE-UP' || orientation === 'FACE-DOWN' || orientation === 'UNKNOWN') {
        break;
      }

      if (orientation === 'PORTRAIT' || orientation === 'PORTRAIT-UPSIDEDOWN') {
        result = 'PORTRAIT';
      } else {
        result = 'LANDSCAPE';
      }
      draft.orientation = result;
      break;
    case SET_DEFAULT_INSTRUCTOR:
      draft.defaultInstructor = action.payload.instructorId;
      break;
    case GET_INSTRUCTOR_LIST_SUCCESS:
      draft.instructorList = action.payload.instructorList;
      const isSelected = action.payload.instructorList
        .find((item: Instructor) => item.id === action.payload.workoutSessionInstructor);
      draft.selectedInstructor = isSelected;
      break;
    case UPDATE_LEADERBOARD_REQUEST:
      draft.isFetchingLeaderboard = true;
      break;
    case UPDATE_LEADERBOARD_SUCCESS:
      const currentLeaderbooardRows = draft.leaderboardRows;
      if (currentLeaderbooardRows.length >= 0) {
        const { leaderboardResponse } = action.payload;
        draft.fullResponseLeaderboardRows = leaderboardResponse.leaderboardRows;
        draft.leaderboardRows = buildLeaderboardRows(draft);
        draft.groupCallLeaderboardRows = leaderboardResponse.groupCallLeaderboardRows;
      }
      draft.isFetchingLeaderboard = false;
      draft.leaderboardFetchedAt = action.payload.date;
      break;
    case UPDATE_LEADERBOARD_FAILURE:
      draft.isFetchingLeaderboard = false;
      break;
    case ENABLE_MIRROR:
      draft.mirrorIsActive = true;
      draft.hasCameraPermission = true;
      break;
    case DISABLE_MIRROR:
      draft.mirrorIsActive = false;
      break;
    case ON_WARMUPORFIRSTEXERCISE_JUST_STARTED_PLAYING:
      draft.warmupOrFirstExerciseHasStartedPlaying = true;
      break;
    case LEADERBOARD_LAYOUT_CHANGED:
      draft.maxAmountOfLeaderboardRows = action.payload.amountOfRows;
      draft.leaderboardRows = buildLeaderboardRows(draft);
      break;
    case NAVIGATE_TO_DASHBOARD_AFTER_REVIEW:
      draft.afterHomeWorkoutReviewRedirectToDashboard = action.payload;
      break;
    case ACTIVATE_LEADERBOARD:
      draft.isLeaderboardActivated = true;
      break;
    case DEACTIVATE_LEADERBOARD:
      draft.isLeaderboardActivated = false;
      break;
    case INVITE_FRIENDS_REQUEST:
      draft.loadingShareLink = true;
      break;
    case INVITE_FRIENDS_SUCCESS:
      draft.loadingShareLink = false;
      break;
    case INVITE_FRIENDS_FAILURE:
      draft.loadingShareLink = false;
      break;
    case MIRROR_SETUP_SCREEN_OPENED:
      draft.hasCameraPermission = (
        action.payload.cameraPermissionRequest === CameraPermissionRequest.NEWLY_GRANTED
        || action.payload.cameraPermissionRequest === CameraPermissionRequest.ALREADY_GRANTED
      );
      if (!draft.hasCameraPermission) {
        draft.mirrorIsActive = false;
      }
      if (action.payload.cameraPermissionRequest === CameraPermissionRequest.NEWLY_GRANTED) {
        draft.mirrorIsActive = true;
      }
      break;
    case PRE_WORKOUT_SCREEN_CLOSE_REQUEST:
      draft.shouldCloseWorkout = true;
      break;
    case PRE_WORKOUT_CANCEL_REQUEST:
      draft.shouldCloseWorkout = true;
      break;
    case HOME_WARMDOWN_STARTED_REQUEST:
      draft.isProcessingHomeWarmdownStarted = true;
      break;
    case HOME_WARMDOWN_STARTED_SUCCESS:
      draft.hasProcessedHomeWarmdownState = true;
      draft.isProcessingHomeWarmdownStarted = false;
      break;
    case HOME_WARMDOWN_STARTED_FAILURE:
      draft.isProcessingHomeWarmdownStarted = false;
      break;
    case SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_REQUEST:
      draft.betaFeatureChangeLoading = true;
      break;
    case SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_SUCCESS:
      draft.betaFeatureChangeLoading = false;
      break;
    case SET_USER_SETTINGS_BENCH_AND_DUMBBELLS_FAILURE:
      draft.betaFeatureChangeLoading = false;
      break;
    case SET_USER_SETTINGS_OPTION_REQUEST:
      draft.betaFeatureChangeLoading = true;
      break;
    case SET_USER_SETTINGS_OPTION_SUCCESS:
      draft.betaFeatureChangeLoading = false;
      break;
    case SET_USER_SETTINGS_OPTION_FAILURE:
      draft.betaFeatureChangeLoading = false;
      break;
    default:
      break;
  }
  return draft;
});

function shouldLoadNextVideo(
  currentSessionSequenceInfo: ISessionSequenceInfo | null,
  nextSessionSequenceInfo: ISessionSequenceInfo | null,
  draft: IHomeWorkoutsStoreState,
): boolean {
  const isCompletelyEmpty = !currentSessionSequenceInfo && !nextSessionSequenceInfo;
  if (isCompletelyEmpty) {
    return false;
  }

  if (!currentSessionSequenceInfo && nextSessionSequenceInfo) {
    return true;
  }

  if (!nextSessionSequenceInfo) {
    return false;
  }

  const videoHasNotChangedSinceLastSequence = currentSessionSequenceInfo
    && currentSessionSequenceInfo.currentSequence.videoFile === nextSessionSequenceInfo.currentSequence.videoFile
    && draft.currentPlayingVideo !== '';
  if (videoHasNotChangedSinceLastSequence) {
    return false;
  }

  return true;
}

function nextAudioTipToPlay(
  draft: IHomeWorkoutsStoreState,
  nextSessionSequenceInfo: ISessionSequenceInfo | null,
): string | null {
  if (!draft.voiceVolume) {
    return null;
  }

  const { currentSessionSequenceInfo } = draft;

  const isCompletelyEmpty = !currentSessionSequenceInfo && !nextSessionSequenceInfo;
  if (isCompletelyEmpty) {
    return null;
  }

  if (!currentSessionSequenceInfo) {
    return null;
  }

  if (!nextSessionSequenceInfo
    || !nextSessionSequenceInfo.currentSequence
    || !nextSessionSequenceInfo.currentSequence.audioTips) {
    return null;
  }

  const sequenceChanged = currentSessionSequenceInfo.currentIndex
    !== nextSessionSequenceInfo.currentIndex;
  if (sequenceChanged) {
    draft.currentSetOfTips = JSON.parse(JSON.stringify(nextSessionSequenceInfo.currentSequence.audioTips));
  }

  const tipMissedTimeFrame = 2500; // bigger value to avoid blank tips, at the risk of cut off tips
  const msIntoSequence = nextSessionSequenceInfo.currentSequence.durationInMilliseconds * 1000
    - nextSessionSequenceInfo.remainingSecondsInSequence * 1000;
  const indexToPlay = draft.currentSetOfTips.findIndex((tip: IAudioTip) => {
    const tipHasMissedPlayWindow = tip.delay - msIntoSequence > tipMissedTimeFrame;
    return tip.delay <= msIntoSequence && !tipHasMissedPlayWindow;
  });

  if (indexToPlay === -1) {
    return null;
  }
  const fileNameToPlay = draft.currentSetOfTips[indexToPlay].fileName;
  draft.currentSetOfTips.splice(0, indexToPlay + 1);
  return fileNameToPlay;
}

function shouldOpenMenuDescription(
  state: IHomeWorkoutsStoreState,
  nextSessionSequenceInfo: ISessionSequenceInfo | null,
): boolean {
  const { currentSessionSequenceInfo } = state;

  const isCompletelyEmpty = !currentSessionSequenceInfo && !nextSessionSequenceInfo;
  if (isCompletelyEmpty) {
    return false;
  }

  if (!currentSessionSequenceInfo) {
    return false;
  }

  if (!nextSessionSequenceInfo) {
    return false;
  }

  if (state.userHasToggledMenuDescription) {
    return state.menuDescriptionIsOpen;
  }

  return currentSessionSequenceInfo.currentIndex === 0;
}

function currentSequenceIsInOrAfter(
  state: IHomeWorkoutsStoreState,
  numberOfSequencesToCheckFor: number,
): boolean {
  const { currentSessionSequenceInfo, homeWorkoutSession } = state;
  if (!currentSessionSequenceInfo || !homeWorkoutSession) {
    return false;
  }
  const { currentIndex } = currentSessionSequenceInfo;
  return currentIndex >= homeWorkoutSession.sequences.length - numberOfSequencesToCheckFor;
}

function currentSequenceIsInOrBefore(
  state: IHomeWorkoutsStoreState,
  numberOfSequencesToCheckFor: number,
): boolean {
  const { currentSessionSequenceInfo, homeWorkoutSession } = state;
  if (!currentSessionSequenceInfo || !homeWorkoutSession) {
    return false;
  }
  const { currentIndex } = currentSessionSequenceInfo;
  return currentIndex <= numberOfSequencesToCheckFor;
}

function inOrBeforeWarmup(draft: IHomeWorkoutsStoreState): boolean {
  if (draft.homeWorkoutSession?.livestreamOptions?.preWorkoutTalk) {
    return currentSequenceIsInOrBefore(
      draft,
      NUMBER_OF_SEQUENCES_UNTIL_WARMUP_WITH_TALK,
    );
  }

  return currentSequenceIsInOrBefore(
    draft,
    NUMBER_OF_SEQUENCES_UNTIL_WARMUP,
  );
}

function getEndtimeWithDurationAdded(homeWorkoutSession: HomeWorkoutSequence, duration: number): Date {
  const copiedEndTime = new Date(homeWorkoutSession.endTime);
  copiedEndTime.setTime(homeWorkoutSession.endTime.getTime() + duration);
  return copiedEndTime;
}

function shouldDuckMusic(currentSessionSequenceInfo: ISessionSequenceInfo | null): boolean {
  if (!currentSessionSequenceInfo) {
    return false;
  }

  if (currentSessionSequenceInfo.currentSequence.videoHasAudio) {
    return true;
  }
  return currentSessionSequenceInfo.currentSequence.phase !== Phase.Train;
}

function buildLeaderboardRows(draft: IHomeWorkoutsStoreState): ILeaderboardRow[] {
  const fullLeaderboardCopy = JSON.parse(JSON.stringify(draft.fullResponseLeaderboardRows));
  return LeaderboardManager.buildLeaderboardRows(
    draft.homeWorkoutId,
    fullLeaderboardCopy,
    draft.maxAmountOfLeaderboardRows,
  );
}

function closeDeviceSetup(draft: IHomeWorkoutsStoreState) {
  draft.deviceSetupIsOpen = false;
  draft.currentPlayingVideo = '';
  draft.shouldLoadNextVideo = true;
}
